import React, { useEffect, useState } from 'react';
import { Box, IconButton, ThemeProvider, Tooltip, useTheme } from '@mui/material';
import { HelpOutline, Logout } from '@mui/icons-material';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import api from '../../api';
import { COLORS, PAGE } from '../../theme';
import { getEffectiveUserId, getMasterToken, isMasterAccessActive } from '../../utils/auth';
import UserSidebarBrand from './UserSidebarBrand';
import UserAvatarButton from './UserAvatarButton';
import UserBottomNav from './UserBottomNav';
import UserOnboarding from './UserOnboarding';
import { getUserNavItems } from './userNavigation';
import { userDarkTheme, userLightTheme } from './userTheme';
import logoBranco from '../../assets/logos/Logo Branco com Laranja.svg';
import { GridOverlay, Sparkle } from '../landing/LandingShapes';
import NotificationBell from '../notificacoes/NotificationBell';
import PageContainer from '../shared/PageContainer';
import { encerrarSessao } from '../../utils/logout';

const UserLayoutBrand = () => {
  const outerTheme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [usuario, setUsuario] = useState(null);
  const userId = getEffectiveUserId();
  const navItems = getUserNavItems(userId);
  const mode = outerTheme.palette.mode === 'dark' ? 'dark' : 'light';
  // No modo de visualização administrativa (master) não alteramos o avatar do
  // cliente: a rota PUT /user/avatar sempre atualiza o usuário autenticado.
  const canEditAvatar = !isMasterAccessActive();

  const handleAvatarUpdated = (avatar) => {
    setUsuario((current) => ({ ...(current || {}), avatar }));
  };

  // Busca nome/avatar apenas para o card de identificação do menu
  useEffect(() => {
    if (!userId) return;
    const masterToken = getMasterToken();
    const headers = masterToken ? { Authorization: `Bearer ${masterToken}` } : {};
    api
      .get(`/clientes/${userId}`, { headers })
      .then((response) => setUsuario(response.data))
      .catch(() => {});
  }, [userId]);

  const handleLogout = async () => {
    await encerrarSessao();
    navigate('/');
  };

  const abrirAjuda = () => window.dispatchEvent(new CustomEvent('zoompro:open-help'));

  return (
    <ThemeProvider theme={mode === 'dark' ? userDarkTheme : userLightTheme}>
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          color: 'text.primary',
          display: 'flex',
          position: 'relative',
        }}
      >
        <UserSidebarBrand
          usuario={usuario}
          onAvatarUpdated={handleAvatarUpdated}
          canEditAvatar={canEditAvatar}
        />

        {/* Menu no topo no mobile */}
        <Box
          sx={{
            display: { xs: 'block', md: 'none' },
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1300,
            bgcolor: 'rgba(28,27,25,0.98)',
            color: COLORS.cream,
            borderBottom: '1px solid rgba(242,238,231,0.12)',
            pt: 'env(safe-area-inset-top, 0px)',
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Box
              component="button"
              type="button"
              onClick={() => navigate('/user/dashboard')}
              aria-label="Ir para o painel"
              sx={{ border: 0, bgcolor: 'transparent', p: 0, cursor: 'pointer', display: 'flex' }}
            >
              <Box component="img" src={logoBranco} alt="Zoompro Studio" sx={{ height: 26 }} />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
              <UserAvatarButton
                usuario={usuario}
                size={30}
                badgeSize={16}
                badgeBorderColor={COLORS.dark}
                editable={canEditAvatar}
                onAvatarUpdated={handleAvatarUpdated}
                containerSx={{ display: { xs: 'inline-flex', sm: 'none' } }}
              />
              <NotificationBell color={COLORS.cream} centralPath="/user/notificacoes" />
              <Tooltip title="Como usar a plataforma" arrow>
                <IconButton
                  onClick={abrirAjuda}
                  aria-label="Como usar a plataforma"
                  sx={{ color: COLORS.cream }}
                >
                  <HelpOutline fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Sair" arrow>
                <IconButton onClick={handleLogout} aria-label="Sair" sx={{ color: COLORS.orange }}>
                  <Logout fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box
            data-tour="user-nav"
            sx={{
              display: 'flex',
              gap: 1,
              overflowX: 'auto',
              px: 2,
              pb: 1.2,
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {navItems.map(({ icon: Icon, short, path, tour }) => {
              const active =
                location.pathname === path ||
                (path === '/user/orders' && location.pathname.startsWith('/user/pedido/'));
              return (
                <Box
                  key={path}
                  component="button"
                  type="button"
                  data-tour={tour}
                  onClick={() => navigate(path)}
                  sx={{
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.6,
                    border: `1px solid ${active ? COLORS.orange : 'rgba(242,238,231,0.2)'}`,
                    bgcolor: active ? COLORS.orange : 'transparent',
                    color: active ? COLORS.white : 'rgba(242,238,231,0.8)',
                    borderRadius: '50px',
                    px: 1.4,
                    py: 0.6,
                    cursor: 'pointer',
                    fontSize: '0.7rem',
                    fontWeight: 800,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                  }}
                >
                  <Icon sx={{ fontSize: 16 }} />
                  {short}
                </Box>
              );
            })}
          </Box>
        </Box>
        <Box
          sx={{
            flexGrow: 1,
            minWidth: 0,
            ml: { xs: 0, md: '280px' },
            minHeight: '100vh',
            position: 'relative',
            pt: { xs: 'calc(env(safe-area-inset-top, 0px) + 112px)', md: 0 },
          }}
        >
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              left: { xs: 0, md: '280px' },
              zIndex: 0,
              pointerEvents: 'none',
            }}
          >
            <GridOverlay />
            <Sparkle
              size={54}
              color={COLORS.orange}
              sx={{ position: 'absolute', top: 120, right: '4%', opacity: 0.16 }}
            />
          </Box>

          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              width: '100%',
              // Padding e largura vêm do padrão único (`PAGE`): a área do cliente
              // e o painel admin têm exatamente a mesma caixa de leitura.
              px: PAGE.paddingX,
              pt: PAGE.paddingY,
              // No mobile sobra a barra de navegação inferior: o `pb` extra não
              // é largura, é área de toque — segue local de propósito.
              pb: { xs: 'calc(90px + env(safe-area-inset-bottom, 0px))', md: PAGE.paddingY.md },
            }}
          >
            <PageContainer>
              <Outlet />
            </PageContainer>
          </Box>
        </Box>

        <UserBottomNav userId={userId} />

        <UserOnboarding />
      </Box>
    </ThemeProvider>
  );
};

export default UserLayoutBrand;
